import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

interface TrueImpactCollapseProps {
  show: boolean;
  className?: string;
  bufferHeight?: number;
  animationLength?: number;
  wrapperClassName?: string;
  children:
    | React.ReactElement
    | React.ReactElement[]
    | React.ReactNode
    | string
    | number;
}

const StyledDiv = styled.div<{ height: string; animationLength: number }>`
  overflow: hidden;
  height: ${({ height }) => height};
  transition: height ${({ animationLength }) => animationLength}s ease-in-out;
`;

// Use padding instead of margin when adding space around the child component.
const TrueImpactCollapse: React.FC<TrueImpactCollapseProps> = ({
  show,
  className,
  children,
  bufferHeight = 0,
  wrapperClassName,
  animationLength = 0.5,
}) => {
  const [height, setHeight] = useState<string>(show ? 'auto' : '0px');
  const contentRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!contentRef.current) return;
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const contentHeight = contentRef.current.scrollHeight + bufferHeight;
    let frame: number;
    let timer: ReturnType<typeof setTimeout>;
    if (show) {
      setHeight(`${contentHeight}px`);
      // Once the animation is done, let the content drive the height so
      // nested collapses/dynamic content are never clipped.
      timer = setTimeout(() => setHeight('auto'), animationLength * 1000);
    } else {
      // Snap from 'auto' to a fixed px height so the transition to 0 animates.
      setHeight(`${contentHeight}px`);
      frame = requestAnimationFrame(() =>
        requestAnimationFrame(() => setHeight('0px')),
      );
    }
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(frame);
    };
  }, [show]);

  return (
    <StyledDiv
      height={height}
      animationLength={animationLength}
      className={wrapperClassName}
    >
      <div ref={contentRef} className={className}>
        {children}
      </div>
    </StyledDiv>
  );
};

export default TrueImpactCollapse;
